import { useState, useEffect, useRef } from 'react';
import { Skeleton } from 'primereact/skeleton';
import moment from 'moment-timezone';
import { DataTable, Column, Button } from 'primereact';
import { Link } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';

import { getQueryResult } from '../../Widget/service/widget.service';
import { getLoginUserId } from '../../../../utils/Helpers';
import './DashboardWidgetRenderer.css';
import {
  adjustmentDispatch,
  adjustmentUndoDispatch,
} from '../../../ProjectManagement/Inventory/services/adjustment.services';
import { getRedirectUrl } from '../Widgets/WidgetRoutes';
import { buildWidgetFreezeTableKey } from '../../../shared/PFPrime/PFFreezeDataTable';
import {
  getChartQuery,
  getCompareToQuery,
  getTableQuery,
  prepareChartQueryWithFilters,
  routingWidgetDefaultCondition,
} from '../../Widget/helpers/query';
import {
  DASHBOARD_RETRY_LIMIT,
  INVENTORY_ADJUSTMENT_OBJECT_ID,
  INVENTORY_ADJUSTMENT_PROJECT_OBJECT_ID,
  INVENTORY_ADJUSTMENT_TYPE,
  INVENTORY_ADJUSTMENT_TYPE_PROJECT,
  INVENTORY_ADJUSTMENT_TYPES,
} from '../../Widget/constant/constant';
import ExportWidgetTableData from '../components/ExportWidgetTableData';
import WidgetGroupedTableRenderer from '../../Widget/components/WidgetGroupedTableRenderer';
import WidgetTableRenderer from '../../Widget/components/WidgetTableRenderer';
import WidgetRenderer from '../../Widget/components/WidgetRenderer';
import { MODULES } from '../../../../constants';
import { encrypt } from '../../../../utils/security.helper';

const DashboardWidgetRenderer = ({
  widgetDetails,
  filterDetails,
  homeFilters,
  dashboardCubeFilters = [],
  applyFilters,
  clearFilters,
  homeFilterDateDimension,
  onClickHandler,
  type,
  dashboardDetails = {},
  setIsShowDelayPopUp = () => {},
  item,
}) => {
  const [tablePagination, setTablePagination] = useState({
    filters: null,
  });
  const [widgetTemplate, setWidgetTemplate] = useState(
    widgetDetails?.widget_template ?? widgetDetails ?? {}
  );
  const [paginator, setPaginator] = useState({
    first: 0,
    currentPage: 0,
    perPage: 10,
  });
  const [orderBy, setOrderBy] = useState({
    columns: '',
    direction: 1,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [refreshWidget, setRefreshWidget] = useState(false);
  const [tabGrpDep, setTabGrpDep] = useState(false);
  const [dashboardLogDetails, SetDashboardLogDetails] = useState({});

  const widgetRetryCountRef = useRef(0);

  let chartQuery = null;
  const [compareToResponse, setCompareToResponse] = useState([]);
  const fetchCompareToData = async () => {
    const formattedQuery = getCompareToQuery(widgetTemplate);
    const query = prepareChartQueryWithFilters({
      query: widgetTemplate,
      formattedQuery,
      filterDetails,
    });

    if (widgetTemplate?.route_widget) {
      query.filters.push(routingWidgetDefaultCondition());
    }

    const response = await getQueryResult(
      query,
      null,
      false,
      widgetDetails?.widget_id !== undefined &&
        widgetDetails?.widget_id !== null &&
        !isNaN(Number(widgetDetails?.widget_id))
        ? encrypt(widgetDetails?.widget_id)
        : undefined
    );
    setCompareToResponse(response);
  };
  if (widgetTemplate.widget_display_type[0] === 'table-report') {
    chartQuery = getTableQuery(widgetTemplate);
  } else {
    chartQuery = getChartQuery(widgetTemplate);
  }

  const toast = useRef(null);
  const [groupExportData, setGroupExportData] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const fetchData = async () => {
    try {
      setLoading(true);
      if (
        widgetTemplate?.custom_properties?.showPerformance ||
        widgetTemplate?.custom_properties?.performanceType
      ) {
        fetchCompareToData();
      }

      if ('user-filter' == widgetTemplate.widget_filter_type && filterDetails) {
        chartQuery.filters.push(filterDetails.userFilterCondition);
        dashboardLogDetails.filter_type = 'user-filter';
        dashboardLogDetails.filter_condition =
          filterDetails.userFilterCondition;
      } else if (
        'company-filter' == widgetTemplate.widget_filter_type &&
        filterDetails
      ) {
        chartQuery.filters.push(filterDetails.companyFilterCondition);
        dashboardLogDetails.filter_type = 'company-filter';
        dashboardLogDetails.filter_condition =
          filterDetails.companyFilterCondition;
      } else if (
        'assigned-to-self' == widgetTemplate.widget_filter_type &&
        filterDetails
      ) {
        chartQuery.filters.push({
          member: 'UserMeta.user_id',
          values: [getLoginUserId()],
          operator: 'equals',
        });
        dashboardLogDetails.filter_type = 'assigned-to-self';
        dashboardLogDetails.filter_condition = [
          {
            member: 'UserMeta.user_id',
            values: [getLoginUserId()],
            operator: 'equals',
          },
        ];
      } else if (
        'company-and-assigned' == widgetTemplate.widget_filter_type &&
        filterDetails
      ) {
        chartQuery.filters.push({
          member: 'UserMeta.user_id',
          values:
            filterDetails?.companyUsers?.length > 0
              ? filterDetails?.companyUsers
              : ['0'],
          operator: 'equals',
        });
        dashboardLogDetails.filter_type = 'company-and-assigned';
        dashboardLogDetails.filter_condition = [
          {
            member: 'UserMeta.user_id',
            values:
              filterDetails?.companyUsers?.length > 0
                ? filterDetails?.companyUsers
                : ['0'],
            operator: 'equals',
          },
        ];
      } else if (
        'user-and-assigned-to-self' == widgetTemplate.widget_filter_type &&
        filterDetails
      ) {
        const userAndAssignToSelfFilter = {
          or: [
            {
              member: 'UserMeta.user_id',
              values: [getLoginUserId()],
              operator: 'equals',
            },
          ],
        };
        dashboardLogDetails.filter_type = 'user-and-assigned-to-self';
        if (filterDetails.userFilterCondition) {
          userAndAssignToSelfFilter.or.push(filterDetails.userFilterCondition);
        }
        chartQuery.filters.push(userAndAssignToSelfFilter);
        dashboardLogDetails.filter_condition = userAndAssignToSelfFilter;
      } else if (
        'opportunity-owner' == widgetTemplate?.widget_filter_type &&
        filterDetails
      ) {
        const opportunityAssignedFilter = {
          member: 'Opportunity.owner_user_id',
          values: [getLoginUserId()],
          operator: 'equals',
        };
        chartQuery.filters.push(opportunityAssignedFilter);
        dashboardLogDetails.filter_type = 'opportunity-owner';
        dashboardLogDetails.filter_condition = [opportunityAssignedFilter];
      }
      dashboardLogDetails.globalFilters = [];
      if (applyFilters && homeFilters.length) {
        homeFilters.forEach(item =>
          dashboardLogDetails.globalFilters.push(item.member)
        );
        chartQuery.filters.push(...homeFilters);
      }
      if (dashboardCubeFilters?.length) {
        chartQuery.filters.push(...dashboardCubeFilters);
      }
      if (applyFilters && homeFilterDateDimension?.length) {
        homeFilterDateDimension.forEach(item =>
          dashboardLogDetails.globalFilters.push(item.dimension)
        );
        homeFilterDateDimension.forEach(timeRecord => {
          chartQuery?.timeDimensions?.push({
            dimension: timeRecord.dimension,
            dateRange: timeRecord.dateRange,
          });
        });
      }
      if (widgetTemplate?.route_widget) {
        chartQuery.filters.push(routingWidgetDefaultCondition());
      }

      dashboardLogDetails.chartQuery = chartQuery;
      dashboardLogDetails.widget_name = widgetTemplate.widget_name;
      dashboardLogDetails.widget_template_id =
        widgetTemplate.widget_template_id;
      dashboardLogDetails.widget_type = widgetTemplate.widget_display_type[0];
      dashboardLogDetails.widget_retry_count = widgetRetryCountRef.current;
      SetDashboardLogDetails({ ...dashboardLogDetails });

      let isSupportTicketTable = false;
      if (widgetTemplate.widget_display_type[0] === 'table-report') {
        isSupportTicketTable =
          widgetTemplate?.custom_properties?.selectedReportTable?.toLocaleLowerCase() ===
          MODULES.SUPPORT.MAIN_CUBE_NAME_LOWERCASE;
      } else {
        isSupportTicketTable =
          widgetTemplate?.custom_properties?.selectedChartTable?.toLocaleLowerCase() ===
          MODULES.SUPPORT.MAIN_CUBE_NAME_LOWERCASE;
      }
      const response = await getQueryResult(
        chartQuery,
        widgetDetails.widget_id,
        isSupportTicketTable,
        widgetDetails?.widget_id !== undefined &&
          widgetDetails?.widget_id !== null &&
          !isNaN(Number(widgetDetails?.widget_id))
          ? encrypt(widgetDetails?.widget_id)
          : undefined
      );
      if (
        response?.error &&
        response.error.includes('wait') &&
        widgetRetryCountRef?.current < DASHBOARD_RETRY_LIMIT
      ) {
        setTimeout(() => {
          if (widgetRetryCountRef?.current < DASHBOARD_RETRY_LIMIT) {
            setRefreshWidget(!refreshWidget);
            widgetRetryCountRef.current =
              (widgetRetryCountRef?.current || 0) + 1;
          }
        }, 4000);
      }
      setChartData(response);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // retry for widget in case got failed it retry count is less than DASHBOARD_RETRY_LIMIT
      if (widgetRetryCountRef?.current < DASHBOARD_RETRY_LIMIT) {
        setTimeout(() => {
          widgetRetryCountRef.current = (widgetRetryCountRef?.current || 0) + 1;
        }, 4000);
      }
      console.error(error);
    }
  };

  const fetchTableData = async (
    params = tablePagination,
    orderByConfiguration = orderBy,
    paginatorConfiguration = paginator
  ) => {
    setTabGrpDep(!tabGrpDep);
    if ('user-filter' == widgetTemplate.widget_filter_type && filterDetails) {
      chartQuery.filters.push(filterDetails.userFilterCondition);
      dashboardLogDetails.filter_type = 'user-filter';
      dashboardLogDetails.filter_condition = filterDetails.userFilterCondition;
    } else if (
      'company-filter' == widgetTemplate.widget_filter_type &&
      filterDetails
    ) {
      chartQuery.filters.push(filterDetails.companyFilterCondition);
      dashboardLogDetails.filter_type = 'company-filter';
      dashboardLogDetails.filter_condition =
        filterDetails.companyFilterCondition;
    } else if (
      'assigned-to-self' == widgetTemplate.widget_filter_type &&
      filterDetails
    ) {
      chartQuery.filters.push({
        member: 'UserMeta.user_id',
        values: [getLoginUserId()],
        operator: 'equals',
      });
      dashboardLogDetails.filter_type = 'assigned-to-self';
      dashboardLogDetails.filter_condition = [
        {
          member: 'UserMeta.user_id',
          values: [getLoginUserId()],
          operator: 'equals',
        },
      ];
    } else if (
      'company-and-assigned' == widgetTemplate.widget_filter_type &&
      filterDetails
    ) {
      chartQuery.filters.push({
        member: 'UserMeta.user_id',
        values:
          filterDetails?.companyUsers?.length > 0
            ? filterDetails?.companyUsers
            : ['0'],
        operator: 'equals',
      });
      dashboardLogDetails.filter_type = 'company-and-assigned';
      dashboardLogDetails.filter_condition = [
        {
          member: 'UserMeta.user_id',
          values:
            filterDetails?.companyUsers?.length > 0
              ? filterDetails?.companyUsers
              : ['0'],
          operator: 'equals',
        },
      ];
    } else if (
      'user-and-assigned-to-self' == widgetTemplate.widget_filter_type &&
      filterDetails
    ) {
      const userAndAssignToSelfFilter = {
        or: [
          {
            member: 'UserMeta.user_id',
            values: [getLoginUserId()],
            operator: 'equals',
          },
        ],
      };
      dashboardLogDetails.filter_type = 'user-and-assigned-to-self';
      if (filterDetails.userFilterCondition) {
        userAndAssignToSelfFilter.or.push(filterDetails.userFilterCondition);
      }
      chartQuery.filters.push(userAndAssignToSelfFilter);
      dashboardLogDetails.filter_condition = userAndAssignToSelfFilter;
    } else if (
      'opportunity-owner' == widgetTemplate?.widget_filter_type &&
      filterDetails
    ) {
      const opportunityAssignedFilter = {
        member: 'Opportunity.owner_user_id',
        values: [getLoginUserId()],
        operator: 'equals',
      };
      chartQuery.filters.push(opportunityAssignedFilter);
      dashboardLogDetails.filter_type = 'opportunity-owner';
      dashboardLogDetails.filter_condition = [opportunityAssignedFilter];
    }
    dashboardLogDetails.globalFilters = [];
    if (applyFilters && homeFilters.length) {
      homeFilters.forEach(item =>
        dashboardLogDetails.globalFilters.push(item.member)
      );
      chartQuery.filters.push(...homeFilters);
    }
    if (dashboardCubeFilters?.length) {
      chartQuery.filters.push(...dashboardCubeFilters);
    }
    if (applyFilters && homeFilterDateDimension?.length) {
      homeFilterDateDimension.forEach(item =>
        dashboardLogDetails.globalFilters.push(item.dimension)
      );
      homeFilterDateDimension.forEach(timeRecord => {
        chartQuery?.timeDimensions?.push({
          dimension: timeRecord.dimension,
          dateRange: timeRecord.dateRange,
        });
      });
    }
    if (orderByConfiguration?.columns && orderByConfiguration?.direction) {
      chartQuery.order = [
        [
          orderByConfiguration?.columns,
          orderByConfiguration?.direction > 0 ? 'asc' : 'desc',
        ],
      ];
    }
    chartQuery.limit = paginatorConfiguration.perPage;
    chartQuery.offset =
      paginatorConfiguration.currentPage * paginatorConfiguration.perPage;
    if (params.filters && Object.keys(params.filters)?.length) {
      const tempFilters = [];
      const tempTimeDimension = [];
      Object.keys(params.filters).forEach(key => {
        if (chartData?.annotation?.dimensions?.[key]?.type == 'time') {
          if (params?.filters?.[key]) {
            params.filters?.[key].constraints.forEach(filterKey => {
              if (filterKey?.value && filterKey.value?.length) {
                tempTimeDimension.push({
                  dimension: key,
                  dateRange: [
                    moment(filterKey.value[0]).format('Y-M-D'),
                    moment(filterKey.value[1]).format('Y-M-D'),
                  ],
                });
              }
            });
          }
        } else if (params.filters?.[key]) {
          params.filters?.[key].constraints.forEach(filterKey => {
            if (filterKey.value) {
              tempFilters.push({
                member: key,
                values: [filterKey.value?.toString()],
                operator: filterKey.matchMode,
              });
            }
          });
        }
      });
      if (tempFilters.length) {
        chartQuery.filters.push(...tempFilters);
      }
      if (tempTimeDimension) {
        chartQuery.timeDimensions.push(...tempTimeDimension);
      }
    }

    if (
      widgetTemplate?.custom_properties?.reportTablePrimaryKey &&
      !widgetTemplate?.report_group_by
    ) {
      chartQuery.dimensions.push(
        widgetTemplate?.custom_properties?.reportTablePrimaryKey
      );
    }

    const isHavingProjectAdjustmentType =
      widgetTemplate?.table_configuration?.some(
        item => item?.measureName === INVENTORY_ADJUSTMENT_TYPE_PROJECT
      );

    if (isHavingProjectAdjustmentType) {
      chartQuery.dimensions.push(INVENTORY_ADJUSTMENT_PROJECT_OBJECT_ID);
    }

    const isHavingInventoryAdjustmentType =
      widgetTemplate?.table_configuration?.some(
        item => item?.measureName === INVENTORY_ADJUSTMENT_TYPE
      );

    if (isHavingInventoryAdjustmentType) {
      chartQuery.dimensions.push(INVENTORY_ADJUSTMENT_OBJECT_ID);
    }
    if (widgetTemplate?.custom_properties?.reportTableSecondaryKey) {
      chartQuery.dimensions.push(
        widgetTemplate?.custom_properties?.reportTableSecondaryKey
      );
    }
    dashboardLogDetails.chartQuery = chartQuery;
    dashboardLogDetails.widget_name = widgetTemplate.widget_name;
    dashboardLogDetails.widget_template_id = widgetTemplate.widget_template_id;
    dashboardLogDetails.widget_type = widgetTemplate.widget_display_type[0];
    SetDashboardLogDetails({ ...dashboardLogDetails });
    chartQuery.total = true;
    const isSupportTicketTable =
      widgetTemplate?.custom_properties?.selectedReportTable?.toLocaleLowerCase() ===
      MODULES.SUPPORT.MAIN_CUBE_NAME_LOWERCASE;
    const response = await getQueryResult(
      chartQuery,
      null,
      isSupportTicketTable,
      widgetDetails?.widget_id !== undefined &&
        widgetDetails?.widget_id !== null &&
        !isNaN(Number(widgetDetails?.widget_id))
        ? encrypt(widgetDetails?.widget_id)
        : undefined
    );
    setChartData(response);
    setTotalCount(response?.total);
  };

  useEffect(() => {
    if (filterDetails) {
      if (widgetTemplate.widget_display_type[0] == 'table-report') {
        setLoading(true);
        fetchTableData(tablePagination, orderBy, paginator)
          .then(() => {
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      } else {
        fetchData();
      }
    } else {
      setLoading(false);
    }
  }, [
    refreshWidget,
    paginator,
    orderBy,
    widgetTemplate,
    filterDetails,
    dashboardCubeFilters,
  ]);

  useEffect(() => {
    if (widgetRetryCountRef?.current >= DASHBOARD_RETRY_LIMIT) {
      setIsShowDelayPopUp(true);
    }
  }, [widgetRetryCountRef?.current]);

  const refreshWidgetHandler = e => {
    e.stopPropagation();
    setRefreshWidget(!refreshWidget);
  };
  const onTableChangeHandler = params => {
    setTablePagination(params);
    fetchTableData(params, orderBy);
  };

  useEffect(() => {
    if (applyFilters) {
      if (widgetTemplate.widget_display_type[0] == 'table-report') {
        setLoading(true);
        Promise.all([fetchTableData(tablePagination, orderBy)])
          .then(() => {
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      } else {
        fetchData();
      }
    }
    if (clearFilters) {
      if (widgetTemplate.widget_display_type[0] == 'table-report') {
        setLoading(true);
        Promise.all([fetchTableData(tablePagination, orderBy)])
          .then(() => {
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      } else {
        fetchData();
      }
    }
  }, [applyFilters, clearFilters]);

  let excelData = [];
  let headingKeys = [];
  let headings = [];
  const tableConfiguration = [];
  widgetTemplate.table_configuration.forEach(record => {
    if (!headingKeys.includes(record.measureName)) {
      headingKeys.push(record.measureName);
      tableConfiguration.push(record);
    }
    if (!headings.includes(record.alice)) headings.push(record.alice);
  });
  excelData.push(headings);
  chartData?.data?.forEach(record => {
    const excelRecord = [];
    headingKeys.forEach(key => {
      const formatter = chartData?.annotation?.dimensions[key]?.meta?.format
        ? eval(chartData?.annotation?.dimensions[key]?.meta?.format)
        : undefined;
      if (formatter) {
        excelRecord.push(formatter(record[key]));
      } else {
        excelRecord.push(record[key]);
      }
    });
    excelData.push(excelRecord);
  });
  const chartLoader = chart_type => {
    if (['pie-chart', 'gauge', 'card'].includes(chart_type)) {
      if (chart_type === 'card') {
        return <Skeleton width="12rem" height="1rem"></Skeleton>;
      } else {
        return <Skeleton shape="circle" size="6.5rem"></Skeleton>;
      }
    } else if (['line-chart', 'bar-chart'].includes(chart_type)) {
      return (
        <div className="skeleton-class align-items-end">
          <Skeleton width="22%" height="100%" className="mr-1"></Skeleton>
          <Skeleton width="22%" height="70%" className="mr-1"></Skeleton>
          <Skeleton width="22%" height="45%" className="mr-1"></Skeleton>
          <Skeleton width="22%" height="24%" className="mr-1"></Skeleton>
        </div>
      );
    } else if (chart_type === 'table-report') {
      const items = Array.from({ length: 5 }, (v, i) => i);

      const bodyTemplate = () => {
        return <Skeleton></Skeleton>;
      };
      return (
        <DataTable
          value={items}
          className="p-datatable-striped"
          style={{ width: '100%' }}
        >
          <Column className="w-3" body={bodyTemplate}></Column>
          <Column className="w-3" body={bodyTemplate}></Column>
          <Column className="w-3" body={bodyTemplate}></Column>
          <Column className="w-3" body={bodyTemplate}></Column>
        </DataTable>
      );
    }
  };

  const handleInventoryDispatch = async id => {
    try {
      setInventoryLoading(true);
      const dispatchResponse = await adjustmentDispatch(id);
      if (dispatchResponse?.status) {
        toast.current.show({
          severity: 'success',
          summary: 'Success!',
          detail: 'Adjustment Updated successfully',
          life: 3000,
        });
        setRefreshWidget(!refreshWidget);
      } else {
        toast.current.show({
          severity: 'error',
          summary: 'Something went wrong.',
          life: 2000,
        });
      }
    } catch (error) {
      console.error(error);
      toast.current.show({
        severity: 'error',
        summary: 'Something went wrong.',
        life: 2000,
      });
    } finally {
      setInventoryLoading(false);
    }
  };

  const handleInventoryUndoDispatch = async id => {
    try {
      setInventoryLoading(true);
      const undoDispatchResponse = await adjustmentUndoDispatch(id);
      if (undoDispatchResponse?.status) {
        toast.current.show({
          severity: 'success',
          summary: 'Success!',
          detail: 'Adjustment Updated successfully',
          life: 3000,
        });
        setRefreshWidget(!refreshWidget);
      } else {
        toast.current.show({
          severity: 'error',
          summary: 'Something went wrong.',
          life: 2000,
        });
      }
    } catch (error) {
      console.error(error);
      toast.current.show({
        severity: 'error',
        summary: 'Something went wrong.',
        life: 2000,
      });
    } finally {
      setInventoryLoading(false);
    }
  };

  const actionButtonsColumn = {
    alice: '',
    measureName: '',
    columnTypeAction: true,
    width: '10%',
    options: {
      sort: false,
      filter: false,

      customBodyRenderLite: dataIndex => {
        let primaryKeyColumn = '';
        let secondaryKeyColumn = '';
        Object.keys(chartData?.annotation?.dimensions)?.forEach(key => {
          if (chartData?.annotation.dimensions[key]?.meta?.primaryKey) {
            primaryKeyColumn = key;
          }
          if (chartData?.annotation?.dimensions[key]?.meta?.secondaryKey) {
            secondaryKeyColumn = key;
          }
        });
        let redirectDetails = null;
        if (primaryKeyColumn) {
          const tableName = primaryKeyColumn?.split('.')[0];
          const id = dataIndex[primaryKeyColumn];
          const secondaryObject = {
            secondaryTable: secondaryKeyColumn?.split('.')[0],
            secondaryId: dataIndex[secondaryKeyColumn],
          };
          redirectDetails = getRedirectUrl(tableName, id, { secondaryObject });
        } else {
          return null;
        }
        const isProjectAllocatedAdjustmentType =
          dataIndex?.[INVENTORY_ADJUSTMENT_TYPE_PROJECT] ===
          INVENTORY_ADJUSTMENT_TYPES.ALLOCATED;
        const isProjectDispatchedAdjustmentType =
          dataIndex?.[INVENTORY_ADJUSTMENT_TYPE_PROJECT] ===
          INVENTORY_ADJUSTMENT_TYPES.DISPATCHED;
        const isInventoryAllocatedAdjustmentType =
          dataIndex?.[INVENTORY_ADJUSTMENT_TYPE] ===
          INVENTORY_ADJUSTMENT_TYPES.ALLOCATED;
        const isInventoryDispatchedAdjustmentType =
          dataIndex?.[INVENTORY_ADJUSTMENT_TYPE] ===
          INVENTORY_ADJUSTMENT_TYPES.DISPATCHED;

        if (isInventoryAllocatedAdjustmentType) {
          const adjustment_object_id =
            dataIndex?.[INVENTORY_ADJUSTMENT_OBJECT_ID];
          return (
            <div className="flex justify-content-center gap-2 m-2">
              <Button
                className="border-none p-button-primary"
                icon="pi pi-refresh"
                text
                outlined
                severity="primary"
                tooltip="Dispatch"
                onClick={() => handleInventoryDispatch(adjustment_object_id)}
                disabled={inventoryLoading}
              ></Button>
            </div>
          );
        }
        if (isInventoryDispatchedAdjustmentType) {
          const adjustment_object_id =
            dataIndex?.[INVENTORY_ADJUSTMENT_OBJECT_ID];
          return (
            <div className="flex justify-content-center gap-2 m-2">
              <Button
                className="border-none p-button-primary"
                icon="pi pi-undo"
                text
                outlined
                severity="primary"
                tooltip="Return to Inventory"
                onClick={() =>
                  handleInventoryUndoDispatch(adjustment_object_id)
                }
                disabled={inventoryLoading}
              ></Button>
            </div>
          );
        }
        if (!redirectDetails) return null;
        return (
          <div className="flex  justify-content-center gap-2 m-2">
            {redirectDetails?.viewUrl && (
              <Link to={redirectDetails.viewUrl} className="text-color-900">
                <i className="pi pi-eye"></i>
              </Link>
            )}
            {redirectDetails?.editUrl && (
              <Link to={redirectDetails.editUrl} className="text-color-900">
                <i className="pi pi-pencil"></i>
              </Link>
            )}
            {isProjectAllocatedAdjustmentType && (
              <>
                <Tooltip target=".dispatch-icon" />
                <i
                  className="pi pi-refresh cursor-pointer text-color-900 dispatch-icon"
                  role="button"
                  tabIndex="0"
                  onClick={() =>
                    handleInventoryDispatch(
                      dataIndex?.[INVENTORY_ADJUSTMENT_PROJECT_OBJECT_ID]
                    )
                  }
                  onKeyDown={e => {
                    if (e?.key === 'Enter' || e?.key === ' ') {
                      handleInventoryDispatch(
                        dataIndex?.[INVENTORY_ADJUSTMENT_PROJECT_OBJECT_ID]
                      );
                    }
                  }}
                  data-pr-tooltip="Dispatch"
                  data-pr-position="top"
                ></i>
              </>
            )}
            {isProjectDispatchedAdjustmentType && (
              <>
                <Tooltip target=".undo-dispatch-icon" />
                <i
                  className="pi pi-undo cursor-pointer text-color-900 undo-dispatch-icon"
                  role="button"
                  tabIndex="0"
                  onClick={() =>
                    handleInventoryUndoDispatch(
                      dataIndex?.[INVENTORY_ADJUSTMENT_PROJECT_OBJECT_ID]
                    )
                  }
                  onKeyDown={e => {
                    if (e?.key === 'Enter' || e?.key === ' ') {
                      handleInventoryUndoDispatch(
                        dataIndex?.[INVENTORY_ADJUSTMENT_PROJECT_OBJECT_ID]
                      );
                    }
                  }}
                  data-pr-tooltip="Return to Inventory"
                  data-pr-position="top"
                ></i>
              </>
            )}
          </div>
        );
      },
    },
  };
  tableConfiguration.unshift(actionButtonsColumn);
  const handleGroupByExport = () => {
    let widget = structuredClone(widgetTemplate);
    if (widget.backToGroupBy) {
      widget.table_configuration = widget.backToGroupBy.table_configuration;
      widget.report_group_by = widget.backToGroupBy.report_group_by;
      delete widget.backToGroupBy;
    } else {
      widget.backToGroupBy = {
        table_configuration: [...widget.table_configuration],
        report_group_by: widget.report_group_by,
      };
      widget.table_configuration.unshift({
        alice: widget?.report_group_by?.alice,
        measureName: widget?.report_group_by?.dimension,
      });
      delete widget.report_group_by;
    }
    setWidgetTemplate(widget);
  };

  return (
    <>
      {type == 'home' && (
        <div className="excel-export-container">
          <div
            className="excel-export-wrapper"
            style={{ paddingBottom: '0.35rem' }}
          >
            {widgetDetails.chart_type == 'table-report' && (
              <div
                className={`mr-3 ${widgetDetails.chart_type == 'table-report' ? 'pt-4' : null}`}
              >
                <ExportWidgetTableData
                  widget={widgetTemplate}
                  groupByHandler={handleGroupByExport}
                  exportData={excelData}
                  groupExportData={groupExportData}
                  filename={`${widgetTemplate?.widget_display_type[0]}_${
                    paginator?.currentPage + 1
                  }_${moment().format('Y_M_D')}`}
                  groupFilename={`${widgetTemplate?.widget_display_type[0]}_groups_${moment().format(
                    'Y_M_D'
                  )}`}
                />
              </div>
            )}
            {widgetTemplate?.widget_display_type?.[0] !==
              'external-link-button' && (
              <div
                className={`${widgetTemplate?.widget_display_type?.[0] === 'table-report' ? 'pt-4 pr-3' : 'pr-1'}`}
              >
                <i
                  className={`${loading ? 'pi pi-sync pi-spin' : 'pi pi-sync'}  text-sm`}
                  style={{ cursor: 'pointer' }}
                  role="button"
                  tabIndex="0"
                  onClick={refreshWidgetHandler}
                  onKeyDown={e => {
                    if (e?.key === 'Enter' || e?.key === ' ') {
                      refreshWidgetHandler();
                    }
                  }}
                ></i>
              </div>
            )}
          </div>
        </div>
      )}
      <div
        style={{
          width: '100%',
          height: widgetDetails?.chart_type === 'gauge' && '100%',
          ...(widgetDetails?.chart_type === 'table-report'
            ? {
                maxHeight: '88%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }
            : {}),
        }}
      >
        {loading ? (
          <div className="chart-loader p-2">
            {chartLoader(widgetTemplate?.widget_display_type?.[0])}
          </div>
        ) : widgetDetails.chart_type == 'table-report' ? (
          <div className="pf-dashboard-widget-table-slot w-full">
            {widgetTemplate?.report_group_by ? (
              <WidgetGroupedTableRenderer
                onGroupExportDataChange={setGroupExportData}
                widgetDetails={widgetTemplate}
                data={chartData?.data}
                tablePagination={tablePagination}
                fetchTableData={fetchTableData}
                annotation={chartData?.annotation?.dimensions}
                totalCount={totalCount}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                paginator={paginator}
                setPaginator={setPaginator}
                filters={[
                  ...(homeFilters || []),
                  ...(dashboardCubeFilters || []),
                ]}
                homeFilterDateDimension={homeFilterDateDimension}
                tabGrpDep={tabGrpDep}
                filterDetails={filterDetails}
                freezeTableKey={buildWidgetFreezeTableKey(
                  {
                    ...widgetTemplate,
                    widget_id:
                      widgetDetails?.widget_id ?? widgetTemplate?.widget_id,
                  },
                  'grouped'
                )}
                freezeFillSlot
              />
            ) : (
              <WidgetTableRenderer
                table_configuration={tableConfiguration}
                data={chartData?.data}
                tablePagination={tablePagination}
                onTableChangeHandler={onTableChangeHandler}
                annotation={chartData?.annotation?.dimensions}
                totalCount={totalCount}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                paginator={paginator}
                setPaginator={setPaginator}
                dashboardLogDetails={dashboardLogDetails}
                dashboardDetails={{
                  ...dashboardDetails,
                  widget_id:
                    widgetDetails?.widget_id ??
                    dashboardDetails?.widget_id ??
                    widgetTemplate?.widget_id,
                }}
                freezeTableKey={buildWidgetFreezeTableKey(
                  {
                    ...widgetTemplate,
                    widget_id:
                      widgetDetails?.widget_id ?? widgetTemplate?.widget_id,
                  },
                  'normal'
                )}
                freezeFillSlot
              />
            )}
          </div>
        ) : (
          <div
            className={`${widgetDetails.chart_type == 'gauge' && 'h-full'} flex`}
          >
            <WidgetRenderer
              widget_type={widgetTemplate?.widget_display_type?.[0]}
              cubeResponse={chartData?.data}
              target={widgetTemplate?.chart_configuration?.measureTarget}
              annotation={chartData?.annotation}
              formikData={widgetTemplate}
              onClickHandler={onClickHandler}
              widgetId={widgetDetails.widget_id}
              showButtonPreview={false}
              dashboardLogDetails={dashboardLogDetails}
              dashboardDetails={dashboardDetails}
              item={item}
              compareToResponse={compareToResponse?.data}
            />
          </div>
        )}
      </div>
      <Toast ref={toast} />
    </>
  );
};

export default DashboardWidgetRenderer;
